/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, May 2020
 */

import { forOwn } from '../../core/lib';

/**
 *  A class that allows you to manipulate HTTP headers in various ways.
 */
export class Headers {
    private headers: Record<string, string> = {};

    constructor();
    /**
     * Parse HTTP headers from a string.
     *
     * @param headers The string to parse.
     * The string must be formatted as a list of `<header>:<value>`
     * pairs separated by a carriage return and a new line.
     * @returns Headers object populated with the headers of the string.
     */
    constructor(headers: string);
    /**
     * Create a Headers instance.
     *
     * @param headers An object containing any HTTP headers
     * you want to pre-populate the instance with.
     */
    constructor(headers: Record<string, string>);
    constructor(headers?: string | Record<string, string>) {
        // Parse the headers string.
        if (typeof headers === 'string') {
            this.parse(headers);
        } else if (headers) {
            forOwn(headers, (value, name) => {
                this.set(name, value);
            });
        }
    }

    /**
     * Create a new Headers instance with the same values.
     *
     * @returns Cloned instance.
     */
    public clone(): Headers {
        return new Headers(this.headers);
    }

    /**
     * Get the value of a header.
     *
     * @param name Name of the header.
     * @returns The value of the header.
     */
    public get(name: string): string {
        return this.headers[name] ?? '';
    }

    /**
     * Set the value of a header.
     *
     * @param name Name of the header.
     * @param value Value of the header.
     */
    public set(name: string, value: string): void {
        this.headers[name.toLowerCase()] = value;
    }

    /**
     * Loop through each header in the collection.
     *
     * @param iterator A callback called for each header in the collection.
     */
    public forEach(iterator: (value: string, key: string) => void): void {
        forOwn(this.headers, iterator);
    }

    /**
     * Adds all headers to the instance.
     *
     * @param headers Headers to merge with the current ones.
     */
    public merge(headers: Headers): void {
        headers.forEach((value, name) => {
            this.set(name, value);
        });
    }

    /**
     * Remove a header.
     *
     * @param name Name of the header.
     */
    public delete(name: string): void {
        delete this.headers[name];
    }

    private parse(headers: string): void {
        // Lines are separated by a carriage-return.
        headers
            .replace(/\r?\n[\t ]+/g, ' ')
            .split(/[\r\n]+/)
            .forEach(line => {
                const [name, value] = line.split(':').map(v => v.trim());
                this.set(name, value);
            });
    }
}



// WEBPACK FOOTER //
// ./src/packages/http/lib/Headers.ts